import React from "react";
import { Input, Label } from "reactstrap";
import "./TabAccessCard.css";

interface TabAccessCardProps {
  tabId: string;
  tabLabel: string;
  metrics: { id: string; label: string }[];
  selectedMetrics: string[];
  isEnabled: boolean;
  exportOnly?: boolean;
  onChange: (tabId: string, metrics: string[]) => void;
}

const TabAccessCard: React.FC<TabAccessCardProps> = ({
  tabId,
  tabLabel,
  metrics,
  selectedMetrics,
  isEnabled,
  exportOnly,
  onChange,
}) => {
  const allMetricIds = metrics.map(m => m.id);
  const allSelected = isEnabled && allMetricIds.length > 0 && allMetricIds.every(id => selectedMetrics.includes(id));
  const someSelected = isEnabled && selectedMetrics.length > 0 && !allSelected;

  const handleToggle = (checked: boolean) => {
    if (checked) {
      onChange(tabId, allMetricIds);
    } else {
      onChange(tabId, []);
    }
  };

  const handleSelectDeselectAll = () => {
    if (allSelected) {
      onChange(tabId, []);
    } else {
      onChange(tabId, allMetricIds);
    }
  };

  const handleMetricToggle = (metricId: string) => {
    const updated = selectedMetrics.includes(metricId)
      ? selectedMetrics.filter(id => id !== metricId)
      : [...selectedMetrics, metricId];
    if (updated.length === 0) {
      onChange(tabId, []);
    } else {
      onChange(tabId, updated);
    }
  };

  return (
    <div className="tab-access-card-wrapper">
      <div className="form-check form-switch tab-access-card-toggle">
        <Input
          type="checkbox"
          className="form-check-input"
          id={`tab-${tabId}`}
          checked={isEnabled}
          onChange={(e) => handleToggle(e.target.checked)}
        />
        <Label className="form-check-label fw-medium" htmlFor={`tab-${tabId}`}>
          {tabLabel}
          {exportOnly && (
            <span className="badge bg-soft-info text-info ms-1 tab-access-export-badge">Export Only</span>
          )}
        </Label>
        {isEnabled && (
          <span className="tab-access-card-counter">
            {selectedMetrics.length}/{metrics.length}
          </span>
        )}
      </div>
      <div className={`tab-access-card-box${!isEnabled ? " tab-access-card-box-disabled" : ""}`}>
        <div className="tab-access-card-header">
          <div className="form-check tab-access-card-select-all">
            <Input
              type="checkbox"
              className="form-check-input"
              id={`tab-select-all-${tabId}`}
              checked={allSelected}
              innerRef={(el: HTMLInputElement | null) => {
                if (el) el.indeterminate = someSelected;
              }}
              disabled={!isEnabled}
              onChange={handleSelectDeselectAll}
            />
            <Label className="form-check-label" htmlFor={`tab-select-all-${tabId}`}>
              {allSelected ? "Deselect All" : "Select All"}
            </Label>
          </div>
        </div>
        <div className="tab-access-card-divider" />
        <div className="tab-access-metrics-grid">
          {metrics.map(metric => (
            <div className="form-check mb-1" key={metric.id}>
              <Input
                type="checkbox"
                className="form-check-input"
                id={`tab-metric-${tabId}-${metric.id}`}
                checked={selectedMetrics.includes(metric.id)}
                disabled={!isEnabled}
                onChange={() => handleMetricToggle(metric.id)}
              />
              <Label className="form-check-label" htmlFor={`tab-metric-${tabId}-${metric.id}`}>
                {metric.label}
              </Label>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TabAccessCard;
